import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import { LinkButton } from '@enamelai/design-system'
import { Icon as ArrowLeftIcon } from '@enamelai/design-system/icons/Arrow_2_left'
import { Icon as ArrowRightIcon } from '@enamelai/design-system/icons/Arrow_2_right'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import ReportDataTable, {
  ReportDataTableColumn,
} from '@components/pages/Reports/shared/ReportDataTable.component'
import ReportExpandableSection from '@components/pages/Reports/shared/ReportExpandableSection.component'
import { useReportInPlaceExpandOptional } from '@components/pages/Reports/shared/ReportInPlaceExpandContext'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DISTRIBUTION_VIEW_TABS } from '@constants/Reports/patientBaseHealth.constants'
import {
  MedicalAlertCategoryRow,
  PATIENT_RISK_COMPLIANCE_FILTERS,
  PATIENT_RISK_COMPLIANCE_KPI_DEFINITIONS,
  UNSCHEDULED_TREATMENT_VIEW_TABS,
  UnscheduledTreatmentView,
} from '@constants/Reports/patientRiskCompliance.constants'
import {
  ReportsDistributionView,
  ReportsKpiMetric,
} from '@dataTypes/reports.type'
import { usePatientRiskComplianceReport } from '@hooks/reports/usePatientRiskComplianceReport'
import { useReportFinanceUiEffects } from '@hooks/reports/useReportFinanceUiEffects'
import {
  hasNonZeroDashboardValues,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import { formatReportKpiFooter } from '@utils/reports/reportKpiFooter.util'
import {
  computeDynamicCountAxis,
  computeDynamicDollarAxis,
  computePercentAxis,
} from '@utils/reports/revenueRealizationFinance.util'
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@components/pages/Reports/shared/ReportDesignSystemCharts.component'

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'
const REPORT_BAR_CHART_MARGIN = { top: 24, right: 12, left: 4, bottom: 0 }
const REPORT_BAR_CATEGORY_GAP = '16%'
const REPORT_YAXIS_WIDTH_WIDE = 46

/**
 * Same margin + Y width as other patient report bar charts; Unscheduled + Overdue
 * both use this so their plot left edge matches each other.
 */
const RISK_BAR_CHART_MARGIN = REPORT_BAR_CHART_MARGIN
const RISK_BAR_Y_AXIS_WIDTH = REPORT_YAXIS_WIDTH_WIDE
const MEDICAL_ALERTS_CARD_ROW_LIMIT = 6

function formatKpiCount(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—'
  return Math.round(value).toLocaleString('en-US')
}

function PatientRiskComplianceReport(): React.ReactElement {
  const expandCtx = useReportInPlaceExpandOptional()
  const medicalAlertsFullViewOnly =
    expandCtx?.expandedSectionId === 'prc-alerts'
  const [overdueView, setOverdueView] =
    useState<ReportsDistributionView>('Count')
  const [unscheduledView, setUnscheduledView] =
    useState<UnscheduledTreatmentView>('Patients')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)

  const { loading, error, data } = usePatientRiskComplianceReport(
    (reportFiltersAndMetrics?.filters ?? {}) as unknown as Record<
      string,
      string
    >,
  )
  useReportFinanceUiEffects({
    loading,
    error,
    errorMessage:
      'Failed to load patient risk and compliance data. Please try again.',
  })

  const kpiItems = useMemo((): ReportsKpiMetric[] => {
    if (!data) {
      return PATIENT_RISK_COMPLIANCE_KPI_DEFINITIONS.map((def) => ({
        id: def.id,
        title: def.title,
        footer: def.footer || '—',
        unitInline: def.unitInline,
        value: loading ? '…' : '—',
        tone: 'neutral',
      }))
    }

    const valueById: Record<string, number | undefined> = {
      'unscheduled-treatments': data.patientsWithUnscheduledTreatments?.value,
      'medical-alerts': data.patientsWithMedicalAlerts?.value,
      'overdue-6': data.patientsOverdue?.value,
    }

    const priorById: Record<string, number | undefined> = {
      'unscheduled-treatments':
        data.patientsWithUnscheduledTreatmentsFooter?.value,
      'medical-alerts': data.patientsWithMedicalAlertsFooter?.value,
      'overdue-6': data.patientsOverdueFooter?.value,
    }

    return PATIENT_RISK_COMPLIANCE_KPI_DEFINITIONS.map((def) => {
      const footerResult = formatReportKpiFooter(
        def.footerMode ?? 'percentChange',
        valueById[def.id],
        priorById[def.id],
      )

      return {
        id: def.id,
        title: def.title,
        footer: footerResult.text,
        tone: footerResult.tone,
        unitInline: def.unitInline,
        value: formatKpiCount(valueById[def.id]),
      }
    })
  }, [data, loading])

  const medicalAlertRows = useMemo((): MedicalAlertCategoryRow[] => {
    if (!data) return []
    return (data.medicalAlertCategories?.rows ?? []).map((row) => ({
      id: row.id,
      category: row.category,
      patients: row.patients,
    }))
  }, [data])

  const medicalAlertsTableRows = useMemo(
    () =>
      medicalAlertsFullViewOnly
        ? medicalAlertRows
        : medicalAlertRows.slice(0, MEDICAL_ALERTS_CARD_ROW_LIMIT),
    [medicalAlertsFullViewOnly, medicalAlertRows],
  )

  const handleMedicalAlertsFullView = (): void => {
    expandCtx?.expand('prc-alerts')
  }

  const handleMedicalAlertsExitFullView = (): void => {
    expandCtx?.exit()
  }

  const unscheduledChartData = useMemo(() => {
    const buckets = data?.unscheduledByProductionBucket?.buckets ?? []
    return buckets.map((row) => ({
      ...row,
      barValue: unscheduledView === 'Patients' ? row.patients : row.revenue,
      topLabel:
        unscheduledView === 'Patients'
          ? String(row.patients)
          : `$${(row.revenue / 1000).toFixed(1)}k`,
    }))
  }, [data, unscheduledView])

  const unscheduledYAxis = useMemo(() => {
    const values = unscheduledChartData.map((row) => row.barValue)
    return unscheduledView === 'Patients'
      ? computeDynamicCountAxis(values)
      : computeDynamicDollarAxis(values)
  }, [unscheduledChartData, unscheduledView])

  const overdueChartData = useMemo(() => {
    const buckets = data?.overdueVisitGrouping?.buckets ?? []
    return buckets.map((row) => ({
      ...row,
      barValue: overdueView === 'Count' ? row.count : row.percentage,
      topLabel:
        overdueView === 'Count' ? String(row.count) : `${row.percentage}%`,
    }))
  }, [data, overdueView])

  const overdueYAxis = useMemo(() => {
    const values = overdueChartData.map((row) => row.barValue)
    return overdueView === 'Count'
      ? computeDynamicCountAxis(values)
      : computePercentAxis(values)
  }, [overdueChartData, overdueView])

  const medicalAlertColumns: ReportDataTableColumn<MedicalAlertCategoryRow>[] =
    useMemo(
      () => [
        { key: 'category', label: 'Allergy category' },
        {
          key: 'patients',
          label: 'Patients',
          headerClassName: 'reports-patient-value-revenue__table-head--num',
          cellClassName: 'reports-patient-value-revenue__table-cell--num',
        },
      ],
      [],
    )

  const kpiEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.patientsWithUnscheduledTreatments.value,
      data?.patientsWithMedicalAlerts.value,
      data?.patientsOverdue.value,
    ),
  )
  const unscheduledEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.unscheduledByProductionBucket?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.patients, row.revenue),
    ),
  )
  const overdueEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.overdueVisitGrouping?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.count, row.percentage),
    ),
  )
  const medicalAlertsEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.medicalAlertCategories?.rows ?? []).some((row) =>
      hasNonZeroDashboardValues(row.patients),
    ),
  )

  return (
    <div className="reports-patient-base-health reports-patient-risk-compliance">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Patient Risk &amp; Compliance">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={PATIENT_RISK_COMPLIANCE_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      <div className="reports-patient-risk-compliance__stack">
        <ReportExpandableSection sectionId="prc-upper">
          {kpiEmpty ? (
            <DashboardSectionEmptyState />
          ) : (
            <ReportKpiGrid items={kpiItems} />
          )}

          {/* Recall Compliance Rate — hidden until recall feature is available. */}

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Patients with Unscheduled Treatment
                </ReportSectionHeading>
                {unscheduledEmpty ? null : (
                  <ReportViewTabs
                    options={UNSCHEDULED_TREATMENT_VIEW_TABS}
                    value={unscheduledView}
                    onChange={(value) =>
                      setUnscheduledView(value as UnscheduledTreatmentView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              <p className="reports-patient-risk-compliance__chart-note">
                Patient count and revenue by treatment value bucket
              </p>
              {unscheduledEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-patient-risk-compliance__chart">
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart
                      key={`unscheduled-${unscheduledView}-${unscheduledChartData.map((r) => r.barValue).join(',')}`}
                      data={unscheduledChartData}
                      margin={RISK_BAR_CHART_MARGIN}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                        tick={REPORT_CHART_AXIS_TICK}
                        height={30}
                      />
                      <YAxis
                        domain={unscheduledYAxis.domain}
                        ticks={unscheduledYAxis.ticks}
                        tickFormatter={unscheduledYAxis.tickFormatter}
                        allowDecimals={unscheduledView === 'Revenue'}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={RISK_BAR_Y_AXIS_WIDTH}
                        tickMargin={2}
                      />
                      <Tooltip
                        formatter={(value: number) =>
                          unscheduledView === 'Patients'
                            ? value
                            : `$${value.toLocaleString('en-US')}`
                        }
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Bar
                        dataKey="barValue"
                        fill="var(--blue-400)"
                        stroke="none"
                        radius={[8, 8, 0, 0]}
                        barSize={100}
                      >
                        <LabelList
                          dataKey="topLabel"
                          position="top"
                          style={{
                            fill: 'var(--gray-600)',
                            fontSize: 12,
                            fontWeight: 500,
                          }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>
        </ReportExpandableSection>

        <div className="reports-patient-risk-compliance__grid">
          <ReportExpandableSection sectionId="prc-overdue">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    Patients Overdue (Last Visit Grouping)
                  </ReportSectionHeading>
                  {overdueEmpty ? null : (
                    <ReportViewTabs
                      options={REPORTS_DISTRIBUTION_VIEW_TABS}
                      value={overdueView}
                      onChange={(value) =>
                        setOverdueView(value as ReportsDistributionView)
                      }
                      className="reports-patient-base-health__view-group"
                    />
                  )}
                </div>
                <p className="reports-patient-risk-compliance__chart-note">
                  Percentage of patients with no completed visit in bucket range
                </p>
                {overdueEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-patient-risk-compliance__chart">
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart
                        key={`overdue-${overdueView}-${overdueChartData.map((r) => r.barValue).join(',')}`}
                        data={overdueChartData}
                        margin={RISK_BAR_CHART_MARGIN}
                        barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                      >
                        <CartesianGrid
                          vertical={false}
                          strokeDasharray="0"
                          stroke={REPORT_CHART_GRID_STROKE}
                        />
                        <XAxis
                          dataKey="label"
                          axisLine={false}
                          tickLine={false}
                          interval={0}
                          tick={REPORT_CHART_AXIS_TICK}
                          height={30}
                        />
                        <YAxis
                          domain={overdueYAxis.domain}
                          ticks={overdueYAxis.ticks}
                          tickFormatter={overdueYAxis.tickFormatter}
                          allowDecimals={overdueView === 'Percentage'}
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                          width={RISK_BAR_Y_AXIS_WIDTH}
                          tickMargin={2}
                        />
                        <Tooltip
                          formatter={(value: number) =>
                            overdueView === 'Count' ? value : `${value}%`
                          }
                          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                        />
                        <Bar
                          dataKey="barValue"
                          fill="var(--blue-400)"
                          radius={[8, 8, 0, 0]}
                          barSize={100}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>

          <ReportExpandableSection sectionId="prc-alerts">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column reports-patient-value-revenue__column--fill">
                <div className="reports-patient-base-health__chart-head">
                  {medicalAlertsFullViewOnly ? (
                    <LinkButton
                      type="button"
                      underline={false}
                      leftIcon={<ArrowLeftIcon aria-hidden />}
                      className="reports-patient-base-health__risk-patients-title reports-patient-base-health__risk-patients-title--back"
                      onClick={handleMedicalAlertsExitFullView}
                      aria-label="Exit full view of patients with medical alerts"
                    >
                      <ReportSectionHeading>
                        Patients with Medical Alerts
                      </ReportSectionHeading>
                    </LinkButton>
                  ) : medicalAlertsEmpty ? (
                    <ReportSectionHeading>
                      Patients with Medical Alerts
                    </ReportSectionHeading>
                  ) : (
                    <LinkButton
                      type="button"
                      underline={false}
                      rightIcon={<ArrowRightIcon aria-hidden />}
                      className="reports-patient-base-health__risk-patients-title"
                      onClick={handleMedicalAlertsFullView}
                      disabled={expandCtx == null}
                      aria-label="Open full view of patients with medical alerts"
                    >
                      <ReportSectionHeading>
                        Patients with Medical Alerts
                      </ReportSectionHeading>
                    </LinkButton>
                  )}
                </div>
                <p className="reports-patient-risk-compliance__chart-note">
                  Top alert categories (count of patients)
                </p>
                {medicalAlertsEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-patient-risk-compliance__opportunity-table">
                    <ReportDataTable
                      key={
                        medicalAlertsFullViewOnly
                          ? 'prc-alerts-fv'
                          : 'prc-alerts-card'
                      }
                      columns={medicalAlertColumns}
                      rows={medicalAlertsTableRows}
                      rowKey={(row) => row.id}
                      className={[
                        'reports-patient-value-revenue__provider-table',
                        'reports-patient-risk-compliance__medical-alerts-table',
                        medicalAlertsFullViewOnly
                          ? 'reports-patient-value-revenue__provider-table--full-view'
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      showFullViewButton={false}
                      showExitFullViewButton={false}
                      showPagination={medicalAlertsFullViewOnly}
                      fullViewSectionId="prc-alerts"
                      fullViewSearchWithFilter
                    />
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>
        </div>
      </div>
    </div>
  )
}

export default PatientRiskComplianceReport
